// Round chat button, bottom-right, like a standard chat widget: chat icon when closed, X when open
// (the panel opens just above it). On phones the scroll-to-top button sits directly above this one.
// The panel (ChatBot.js + its data) loads in the background once the page is idle, or on first touch,
// so it opens instantly; it then stays mounted (just hidden), so the conversation survives closing it.
import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X } from 'lucide-react';
import { useDarkMode } from './DarkModeContext';
import { logEvent } from './analytics';

const ChatBot = lazy(() => import('./ChatBot'));

export default function ChatLauncher() {
  const { darkMode } = useDarkMode();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Pages are pre-rendered in light mode and React keeps those classes when it takes over, so the dark colours
  // only apply after this first effect re-renders the button.
  const [mounted, setMounted] = useState(false);
  const dark = mounted && darkMode;
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    // after the page has settled, so it never competes with the first paint
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
    const t = setTimeout(() => idle(() => setLoaded(true)), 3000);
    return () => clearTimeout(t);
  }, []);

  if (pathname.startsWith('/admin') || pathname.startsWith('/pay')) return null;

  const toggle = () => {
    setLoaded(true);
    if (!open) logEvent('Chat', 'Open', pathname);
    setOpen(!open);
  };

  return (
    <>
      {loaded && <Suspense fallback={null}><ChatBot open={open} onClose={close} /></Suspense>}
      <button onClick={toggle} onPointerEnter={() => setLoaded(true)} onTouchStart={() => setLoaded(true)}
        aria-label={open ? 'Close chat' : 'Chat with Christian'} aria-expanded={open}
        style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        className={`fixed right-4 lg:!bottom-6 lg:right-6 z-[61] w-14 h-14 flex items-center justify-center rounded-full shadow-lg transition-transform duration-200 hover:scale-105 active:scale-95 ${
          dark ? 'bg-white text-black' : 'bg-black text-white'}`}>
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
}
